import { useEffect, useRef, useState, type PointerEvent, type RefObject } from 'react';

const PANEL_WIDTH_KEY = 'naia.panel.width';

export interface SplitPanelApi {
  isWide: boolean;
  panelWidth: number;
  splitRef: RefObject<HTMLDivElement | null>;
  onDividerPointerDown: (e: PointerEvent) => void;
  onDividerPointerMove: (e: PointerEvent) => void;
  onDividerPointerUp: () => void;
}

export function useSplitPanel(): SplitPanelApi {
  const [isWide, setIsWide] = useState(() => window.matchMedia('(min-width: 900px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 900px)');
    const onChange = (e: MediaQueryListEvent) => setIsWide(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const [panelWidth, setPanelWidth] = useState<number>(() => {
    try {
      const w = Number(localStorage.getItem(PANEL_WIDTH_KEY));
      return Number.isFinite(w) && w >= 320 ? w : 520;
    } catch { return 520; }
  });
  const splitRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const onDividerPointerDown = (e: PointerEvent) => {
    dragging.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onDividerPointerMove = (e: PointerEvent) => {
    if (!dragging.current || !splitRef.current) return;
    const rect = splitRef.current.getBoundingClientRect();
    const w = Math.min(Math.max(rect.right - e.clientX, 320), Math.max(rect.width - 360, 320));
    setPanelWidth(w);
  };
  const onDividerPointerUp = () => {
    if (!dragging.current) return;
    dragging.current = false;
    setPanelWidth((w) => {
      try { localStorage.setItem(PANEL_WIDTH_KEY, String(Math.round(w))); } catch { /* ignore */ }
      return w;
    });
  };

  return { isWide, panelWidth, splitRef, onDividerPointerDown, onDividerPointerMove, onDividerPointerUp };
}
